/**
 * Mock Data for Agents Section (Full Redesign)
 *
 * 12 agents: 10 builtin + 2 custom
 * Cross-references Studio tools, skills, and models.
 */

// ── Types ──────────────────────────────────────────────────────────────────

// misc-cluster-aaa #581 (AGENTS-HUB-014): align the tenant-facing
// AgentStatus vocabulary with the catalog-collapse trigger (bU8a:1720),
// which only persists `draft|published`. `active|paused|archived` are
// retained for the sysadmin-side lifecycle bar and any legacy mock data
// that still references them, but the Hub filter now exposes
// `published` so tenants can find their live agents.
export type AgentStatus =
  | 'active'
  | 'draft'
  | 'published'
  | 'paused'
  | 'archived';
export type AgentOwnership = 'platform' | 'company' | 'team' | 'personal';
export type AgentModelTask = 'chat' | 'classify' | 'code';
export type AgentAutonomy = 'read_only' | 'assisted' | 'approval_required' | 'auto_execute';
export type AgentTrigger = 'conversational' | 'scheduled' | 'event';
export type AgentCategory = 'operations' | 'maintenance' | 'safety' | 'compliance' | 'documentation' | 'analytics' | 'custom';
export type AgentVisibility = 'private' | 'team' | 'org_wide';
export type GuardrailProfile = 'basic' | 'standard' | 'maximum';

export interface AgentMetrics {
  totalQueries: number;
  avgResponseMs: number;
  successRate: number;
  satisfactionScore: number;
}

export interface AgentExecution {
  id: string;
  query: string;
  status: 'success' | 'error' | 'timeout' | 'approval_pending';
  durationMs: number;
  toolCalls: number;
  timestamp: string;
}

export interface AgentExecutionStep {
  id: string;
  type: 'llm_call' | 'tool_call' | 'tool_result' | 'approval_request' | 'error' | 'checkpoint';
  name: string;
  durationMs: number;
  status: 'success' | 'error' | 'pending';
  input?: string;
  output?: string;
}

export interface MockAgent {
  id: string;
  name: string;
  description: string;
  icon: string;
  iconColor: string;
  category: AgentCategory;
  status: AgentStatus;
  isBuiltin: boolean;
  ownership: AgentOwnership;
  modelTask: AgentModelTask;
  modelId: string;
  systemPrompt: string;
  maxSteps: number;
  autonomyLevel: AgentAutonomy;
  triggerModes: AgentTrigger[];
  tools: string[];
  skills: string[];
  knowledgeSources: string[];
  guardrailProfile: GuardrailProfile;
  tags: string[];
  visibility: AgentVisibility;
  metrics: AgentMetrics;
  createdBy: string;
  createdByName?: string | null;
  version: string;
  createdAt: string;
  updatedAt: string;
}

// ── Label Maps ─────────────────────────────────────────────────────────────

export const AGENT_STATUS_STYLES: Record<AgentStatus, string> = {
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  // misc-cluster-aaa #581: published is the tenant-facing live state.
  published: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  draft: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  paused: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-500',
  archived: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400',
};

export const AGENT_OWNERSHIP_STYLES: Record<AgentOwnership, { label: string; color: string }> = {
  platform: { label: 'Platform', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  company: { label: 'Company', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  team: { label: 'Team', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  personal: { label: 'Personal', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
};

export const AGENT_AUTONOMY_STYLES: Record<AgentAutonomy, { label: string; color: string }> = {
  read_only: { label: 'Read Only', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400' },
  assisted: { label: 'Assisted', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  approval_required: { label: 'Approval', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  auto_execute: { label: 'Auto', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
};

export const AGENT_CATEGORY_LABELS: Record<AgentCategory, { label: string; color: string }> = {
  operations: { label: 'Operations', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  maintenance: { label: 'Maintenance', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20' },
  safety: { label: 'Safety', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  compliance: { label: 'Compliance', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  documentation: { label: 'Documentation', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  analytics: { label: 'Analytics', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  custom: { label: 'Custom', color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20' },
};

/**
 * Fallback style used whenever the agent.category coming back from the API
 * is not one of the seven known `AgentCategory` keys. The pre-fix UI did
 * an unguarded `AGENT_CATEGORY_LABELS[agent.category].color` which crashed
 * the Agents Hub / Gallery / Detail routes the moment the server emitted
 * a category like `general` (see #1107). Keep this in lockstep with the
 * `custom` style so the visual is unsurprising.
 */
export const AGENT_CATEGORY_FALLBACK_STYLE = {
  label: 'Other',
  color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
} as const;

/**
 * Safe lookup that always returns a defined `{ label, color }` pair.
 * Use this everywhere an agent row is rendered — never dereference
 * `AGENT_CATEGORY_LABELS[agent.category]` directly because `agent.category`
 * is a server-typed string and the union here is best-effort.
 */
export function getAgentCategoryStyle(
  category: string | null | undefined,
): { label: string; color: string } {
  if (!category) return AGENT_CATEGORY_FALLBACK_STYLE;
  const hit = (AGENT_CATEGORY_LABELS as Record<string, { label: string; color: string }>)[category];
  return hit ?? AGENT_CATEGORY_FALLBACK_STYLE;
}

// Same defect class as #1107 (founder hit it live on /agents 2026-05-23).
// AGENT_OWNERSHIP_STYLES + AGENT_AUTONOMY_STYLES are typed unions over a
// best-effort key set, but the server can emit values outside the union
// (legacy migrations, canonical-9 cascade, custom-agent drafts). Without
// safe helpers an unguarded `STYLES[value].color` returns undefined and
// the badge crashes the whole route.

export const AGENT_OWNERSHIP_FALLBACK_STYLE = {
  label: 'Unknown',
  color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
} as const;

export function getAgentOwnershipStyle(
  ownership: string | null | undefined,
): { label: string; color: string } {
  if (!ownership) return AGENT_OWNERSHIP_FALLBACK_STYLE;
  const hit = (AGENT_OWNERSHIP_STYLES as Record<string, { label: string; color: string }>)[ownership];
  return hit ?? AGENT_OWNERSHIP_FALLBACK_STYLE;
}

export const AGENT_AUTONOMY_FALLBACK_STYLE = {
  label: 'Unknown',
  color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400',
} as const;

// The backend persists the UI's `assisted` autonomy level under its canonical
// alias `draft_only` (custom-agents PUT transforms assisted→draft_only so the
// runtime/audit pipeline has one vocabulary). On read the UI gets `draft_only`,
// which isn't a key in AGENT_AUTONOMY_STYLES — without this alias the badge fell
// back to "Unknown" everywhere after a user set Autonomy = Assisted. Normalize
// the alias back so the round-trip displays the level the user actually chose.
const AUTONOMY_ALIASES: Record<string, AgentAutonomy> = {
  draft_only: 'assisted',
};

export function getAgentAutonomyStyle(
  autonomy: string | null | undefined,
): { label: string; color: string } {
  if (!autonomy) return AGENT_AUTONOMY_FALLBACK_STYLE;
  const normalized = AUTONOMY_ALIASES[autonomy] ?? autonomy;
  const hit = (AGENT_AUTONOMY_STYLES as Record<string, { label: string; color: string }>)[normalized];
  return hit ?? AGENT_AUTONOMY_FALLBACK_STYLE;
}

export const TRIGGER_LABELS: Record<AgentTrigger, { label: string; icon: string }> = {
  conversational: { label: 'Chat', icon: 'MessageSquare' },
  scheduled: { label: 'Scheduled', icon: 'Clock' },
  event: { label: 'Event', icon: 'Zap' },
};

// ── Mock Agents ────────────────────────────────────────────────────────────

export const MOCK_AGENTS: MockAgent[] = [
  {
    id: 'agent-ask-argus',
    name: 'Ask Argus',
    description: 'General-purpose AI assistant with conversation management, clarification, and response formatting. The UX shell for all user interactions.',
    icon: 'bot', iconColor: 'blue',
    category: 'operations',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: `You are Ask Argus, the primary AI assistant for an enterprise industrial facility.

## Core Responsibilities
- Answer questions about equipment, processes, and operations
- Search and cite documents with precision
- Route complex queries to specialist agents
- Ask clarifying questions when uncertain

## Rules
- **Never guess** — ask for clarification when uncertain
- **Always cite sources** with document name, revision, and page
- **Be concise** — prefer structured responses over long paragraphs
- **Safety first** — flag any safety-critical content immediately`,
    maxSteps: 10,
    autonomyLevel: 'assisted',
    triggerModes: ['conversational'],
    tools: ['builtin.search_knowledge', 'builtin.calculate', 'builtin.get_current_time', 'builtin.add_memory', 'builtin.search_memories', 'builtin.view_skill', 'iq.asset.search', 'iq.asset.getById', 'iq.document.search', 'iq.document.getContent'],
    skills: ['skill-docqa', 'skill-maintenance'],
    knowledgeSources: ['all'],
    guardrailProfile: 'standard',
    tags: ['general', 'chat', 'primary'],
    visibility: 'org_wide',
    metrics: { totalQueries: 12847, avgResponseMs: 2800, successRate: 0.96, satisfactionScore: 0.89 },
    createdBy: 'System',
    version: '3.2.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-15T00:00:00Z',
  },
  {
    id: 'agent-maintenance',
    name: 'Maintenance Intelligence',
    description: 'Equipment health monitoring, failure prediction, PM scheduling, and maintenance trend analysis.',
    icon: 'wrench', iconColor: 'cyan',
    category: 'maintenance',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: `You are a Maintenance Intelligence agent specializing in equipment health analysis.

## Capabilities
- Analyze vibration, temperature, and oil analysis data
- Calculate MTBF/MTTR trends
- Predict equipment failures
- Recommend preventive maintenance actions

## Rules
- Always cite specific data points with timestamps
- Never recommend skipping scheduled PMs without supervisor approval
- Include confidence level for all predictions`,
    maxSteps: 8,
    autonomyLevel: 'assisted',
    triggerModes: ['conversational', 'scheduled', 'event'],
    tools: ['iq.asset.search', 'iq.asset.getById', 'iq.asset.getHistory', 'iq.telemetry.getCurrent', 'iq.telemetry.getHistory', 'iq.analytics.getTrend'],
    skills: ['skill-maintenance'],
    knowledgeSources: ['maintenance-docs'],
    guardrailProfile: 'standard',
    tags: ['equipment', 'predictive', 'vibration', 'PM'],
    visibility: 'org_wide',
    metrics: { totalQueries: 4560, avgResponseMs: 3200, successRate: 0.93, satisfactionScore: 0.91 },
    createdBy: 'System',
    version: '2.1.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-10T00:00:00Z',
  },
  {
    id: 'agent-troubleshoot',
    name: 'Troubleshooting',
    description: 'Guided playbooks for equipment diagnosis, root cause analysis, and resolution recommendations.',
    icon: 'search', iconColor: 'orange',
    category: 'maintenance',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Troubleshooting agent. Guide users through systematic diagnosis using playbooks and historical data.',
    maxSteps: 12,
    autonomyLevel: 'assisted',
    triggerModes: ['conversational', 'event'],
    tools: ['iq.asset.search', 'iq.asset.getById', 'iq.asset.getHistory', 'iq.telemetry.getCurrent', 'iq.document.search', 'iq.document.getContent'],
    skills: ['skill-maintenance', 'skill-docqa'],
    knowledgeSources: ['maintenance-docs', 'troubleshooting-guides'],
    guardrailProfile: 'standard',
    tags: ['diagnosis', 'root-cause', 'playbooks'],
    visibility: 'org_wide',
    metrics: { totalQueries: 2890, avgResponseMs: 4100, successRate: 0.91, satisfactionScore: 0.88 },
    createdBy: 'System',
    version: '1.4.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-03-28T00:00:00Z',
  },
  {
    id: 'agent-docs',
    name: 'Documentation & SOP',
    description: 'Document Q&A with precise citations from SOPs, manuals, P&IDs, and engineering drawings.',
    icon: 'file-text', iconColor: 'blue',
    category: 'documentation',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Documentation agent. Answer questions by searching and citing specific documents.',
    maxSteps: 6,
    autonomyLevel: 'read_only',
    triggerModes: ['conversational'],
    tools: ['iq.document.search', 'iq.document.getContent', 'iq.document.getVersions', 'builtin.search_knowledge'],
    skills: ['skill-docqa', 'skill-safety'],
    knowledgeSources: ['all-docs'],
    guardrailProfile: 'standard',
    tags: ['documents', 'SOPs', 'citations', 'manuals'],
    visibility: 'org_wide',
    metrics: { totalQueries: 8320, avgResponseMs: 2100, successRate: 0.97, satisfactionScore: 0.92 },
    createdBy: 'System',
    version: '1.5.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-05T00:00:00Z',
  },
  {
    id: 'agent-compliance',
    name: 'Compliance Monitor',
    description: 'ITAR/CMMC/HIPAA status checking, certification tracking, and compliance gap analysis.',
    icon: 'shield-check', iconColor: 'violet',
    category: 'compliance',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Compliance Monitor agent. Track regulatory compliance and flag gaps.',
    maxSteps: 6,
    autonomyLevel: 'read_only',
    triggerModes: ['conversational', 'scheduled', 'event'],
    tools: ['iq.document.search', 'iq.document.list'],
    skills: ['skill-compliance', 'skill-docqa'],
    knowledgeSources: ['compliance-docs'],
    guardrailProfile: 'maximum',
    tags: ['ITAR', 'CMMC', 'HIPAA', 'regulatory'],
    visibility: 'org_wide',
    metrics: { totalQueries: 1560, avgResponseMs: 1800, successRate: 0.99, satisfactionScore: 0.94 },
    createdBy: 'System',
    version: '1.2.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-01T00:00:00Z',
  },
  {
    id: 'agent-safety',
    name: 'Safety & LOTO',
    description: 'Lock-Out/Tag-Out procedures, safety permits, PPE requirements, and hazard assessments.',
    icon: 'alert-triangle', iconColor: 'red',
    category: 'safety',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Safety agent. NEVER abbreviate LOTO procedures. Include ALL ANSI Z535.6 signal words.',
    maxSteps: 6,
    autonomyLevel: 'read_only',
    triggerModes: ['conversational', 'event'],
    tools: ['iq.asset.get', 'iq.asset.getDocuments', 'iq.document.search', 'iq.document.getContent'],
    skills: ['skill-safety'],
    knowledgeSources: ['safety-docs'],
    guardrailProfile: 'maximum',
    tags: ['LOTO', 'safety', 'PPE', 'permits'],
    visibility: 'org_wide',
    metrics: { totalQueries: 980, avgResponseMs: 2500, successRate: 0.98, satisfactionScore: 0.95 },
    createdBy: 'System',
    version: '1.3.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-03-20T00:00:00Z',
  },
  {
    id: 'agent-work-order',
    name: 'Work Order Planner',
    description: 'Work order creation, scheduling optimization, resource allocation, and parts requirements.',
    icon: 'clipboard-check', iconColor: 'amber',
    category: 'operations',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Work Order Planner. Create detailed work orders with labor, parts, permits, and scheduling.',
    maxSteps: 10,
    autonomyLevel: 'approval_required',
    triggerModes: ['conversational'],
    tools: ['iq.asset.get', 'iq.cmms.searchWorkOrders', 'iq.cmms.createWorkOrder', 'iq.cmms.updateWorkOrder', 'ext.sap.searchParts', 'ext.sap.checkStock'],
    skills: ['skill-work-planning', 'skill-parts'],
    knowledgeSources: ['maintenance-docs'],
    guardrailProfile: 'standard',
    tags: ['work-orders', 'scheduling', 'resources'],
    visibility: 'org_wide',
    metrics: { totalQueries: 3670, avgResponseMs: 3600, successRate: 0.94, satisfactionScore: 0.87 },
    createdBy: 'System',
    version: '1.1.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-12T00:00:00Z',
  },
  {
    id: 'agent-parts',
    name: 'Parts & Inventory',
    description: 'Parts lookup, inventory levels, alternative parts, cross-referencing, and procurement assistance.',
    icon: 'database', iconColor: 'teal',
    category: 'operations',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Parts agent. Find parts, check stock, and suggest alternatives.',
    maxSteps: 6,
    autonomyLevel: 'read_only',
    triggerModes: ['conversational', 'event'],
    tools: ['iq.asset.get', 'iq.asset.getDocuments', 'ext.sap.searchParts', 'ext.sap.getPartDetails', 'ext.sap.checkStock'],
    skills: ['skill-parts'],
    knowledgeSources: ['parts-catalog'],
    guardrailProfile: 'basic',
    tags: ['parts', 'inventory', 'OEM', 'procurement'],
    visibility: 'org_wide',
    metrics: { totalQueries: 2010, avgResponseMs: 1900, successRate: 0.95, satisfactionScore: 0.86 },
    createdBy: 'System',
    version: '1.0.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-03-10T00:00:00Z',
  },
  {
    id: 'agent-reliability',
    name: 'Reliability',
    description: 'MTBF/MTTR analysis, failure mode analysis, reliability-centered maintenance, and equipment ranking.',
    icon: 'bar-chart', iconColor: 'emerald',
    category: 'analytics',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Reliability agent. Analyze failure patterns and recommend RCM strategies.',
    maxSteps: 8,
    autonomyLevel: 'read_only',
    triggerModes: ['conversational', 'scheduled'],
    tools: ['iq.asset.get', 'iq.asset.getHistory', 'iq.telemetry.getHistory', 'iq.analytics.getTrend'],
    skills: ['skill-maintenance'],
    knowledgeSources: ['maintenance-docs'],
    guardrailProfile: 'standard',
    tags: ['MTBF', 'MTTR', 'RCM', 'failure-analysis'],
    visibility: 'org_wide',
    metrics: { totalQueries: 780, avgResponseMs: 5200, successRate: 0.92, satisfactionScore: 0.90 },
    createdBy: 'System',
    version: '1.0.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-03-15T00:00:00Z',
  },
  {
    id: 'agent-shift-handover',
    name: 'Shift Handover',
    description: 'Automated shift reports: alarms, work completed, open issues, safety observations, and handover notes.',
    icon: 'notebook', iconColor: 'amber',
    category: 'operations',
    status: 'active',
    isBuiltin: true,
    ownership: 'platform',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'You are a Shift Handover agent. Generate comprehensive shift reports collecting all relevant operational data.',
    maxSteps: 10,
    autonomyLevel: 'assisted',
    triggerModes: ['conversational', 'scheduled'],
    tools: ['iq.asset.get', 'iq.telemetry.getAlarms', 'iq.cmms.searchWorkOrders', 'custom.send_shift_notification'],
    skills: ['skill-shift-report'],
    knowledgeSources: [],
    guardrailProfile: 'standard',
    tags: ['shift', 'handover', 'reports', 'alarms'],
    visibility: 'org_wide',
    metrics: { totalQueries: 4200, avgResponseMs: 4800, successRate: 0.97, satisfactionScore: 0.93 },
    createdBy: 'System',
    version: '1.0.0',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-15T00:00:00Z',
  },
  // Custom agents
  {
    id: 'agent-custom-vibration',
    name: 'Vibration Alert Agent',
    description: 'Custom: Monitors vibration thresholds on critical rotating equipment and creates tickets when anomalies detected.',
    icon: 'radio', iconColor: 'pink',
    category: 'maintenance',
    status: 'draft',
    isBuiltin: false,
    ownership: 'team',
    modelTask: 'chat',
    modelId: 'argus-industrial',
    systemPrompt: 'Monitor vibration data for critical equipment. Alert when readings exceed ISO 10816 thresholds.',
    maxSteps: 5,
    autonomyLevel: 'approval_required',
    triggerModes: ['event'],
    tools: ['iq.asset.get', 'iq.telemetry.getCurrent', 'iq.telemetry.getHistory', 'custom.check_valve_status'],
    skills: ['skill-maintenance', 'skill-vibration-analysis'],
    knowledgeSources: ['vibration-standards'],
    guardrailProfile: 'standard',
    tags: ['vibration', 'monitoring', 'alerts', 'ISO 10816'],
    visibility: 'team',
    metrics: { totalQueries: 34, avgResponseMs: 3400, successRate: 0.88, satisfactionScore: 0.85 },
    createdBy: 'Data Science Team',
    version: '0.3.0',
    createdAt: '2026-04-01T00:00:00Z',
    updatedAt: '2026-04-16T00:00:00Z',
  },
  {
    id: 'agent-custom-shift-report',
    name: 'Unit 3 Shift Report',
    description: 'Custom: Tailored shift reports specifically for Unit 3 boiler operations with specific KPIs and alarm filters.',
    icon: 'sparkles', iconColor: 'rose',
    category: 'operations',
    status: 'active',
    isBuiltin: false,
    ownership: 'team',
    modelTask: 'chat',
    modelId: 'argus-fast',
    systemPrompt: 'Generate shift reports for Unit 3 boiler operations. Include boiler efficiency, steam output, and water treatment KPIs.',
    maxSteps: 8,
    autonomyLevel: 'auto_execute',
    triggerModes: ['conversational', 'scheduled'],
    tools: ['iq.asset.get', 'iq.telemetry.getCurrent', 'iq.telemetry.getAlarms', 'iq.cmms.searchWorkOrders', 'custom.send_shift_notification'],
    skills: ['skill-shift-report'],
    knowledgeSources: ['unit-3-docs'],
    guardrailProfile: 'basic',
    tags: ['unit-3', 'boiler', 'shift-report', 'KPIs'],
    visibility: 'team',
    metrics: { totalQueries: 120, avgResponseMs: 6100, successRate: 0.83, satisfactionScore: 0.80 },
    createdBy: 'Admin',
    version: '1.0.0',
    createdAt: '2026-03-25T00:00:00Z',
    updatedAt: '2026-04-17T00:00:00Z',
  },
];

// ── Mock Executions ────────────────────────────────────────────────────────

export const MOCK_EXECUTIONS: AgentExecution[] = [
  { id: 'exec-1', query: 'What is the vibration trend on pump P-401?', status: 'success', durationMs: 2800, toolCalls: 3, timestamp: '2026-04-18T09:15:00Z' },
  { id: 'exec-2', query: 'Create a work order for bearing replacement on C-201', status: 'success', durationMs: 4200, toolCalls: 5, timestamp: '2026-04-18T08:45:00Z' },
  { id: 'exec-3', query: 'Show me the LOTO procedure for heat exchanger HX-301', status: 'success', durationMs: 1800, toolCalls: 2, timestamp: '2026-04-18T08:30:00Z' },
  { id: 'exec-4', query: 'Check compliance status against CMMC Level 2', status: 'success', durationMs: 3100, toolCalls: 4, timestamp: '2026-04-18T07:55:00Z' },
  { id: 'exec-5', query: 'Generate shift handover report for Boiler Room A', status: 'success', durationMs: 8200, toolCalls: 8, timestamp: '2026-04-18T06:00:00Z' },
  { id: 'exec-6', query: 'Find alternative parts for valve FV-401', status: 'error', durationMs: 12000, toolCalls: 2, timestamp: '2026-04-17T16:30:00Z' },
  { id: 'exec-7', query: 'What is the MTBF for compressor fleet?', status: 'success', durationMs: 5200, toolCalls: 4, timestamp: '2026-04-17T14:20:00Z' },
  { id: 'exec-8', query: 'Update PM schedule for turbine T-101', status: 'approval_pending', durationMs: 3500, toolCalls: 3, timestamp: '2026-04-17T11:00:00Z' },
];

export const MOCK_EXECUTION_STEPS: AgentExecutionStep[] = [
  { id: 'step-1', type: 'llm_call', name: 'Intent Classification', durationMs: 120, status: 'success', output: 'intent: maintenance_query' },
  { id: 'step-2', type: 'tool_call', name: 'iq.asset.search', durationMs: 32, status: 'success', input: '{"query": "pump P-401"}', output: '{"id": "asset-p401", "tag": "P-401"}' },
  { id: 'step-3', type: 'tool_call', name: 'iq.telemetry.getHistory', durationMs: 180, status: 'success', input: '{"assetId": "asset-p401", "param": "vibration"}' },
  { id: 'step-4', type: 'tool_call', name: 'iq.analytics.getTrend', durationMs: 250, status: 'success', input: '{"assetId": "asset-p401", "metric": "vibration_rms"}' },
  { id: 'step-5', type: 'llm_call', name: 'Response Generation', durationMs: 2200, status: 'success' },
  { id: 'step-6', type: 'checkpoint', name: 'Faithfulness Check', durationMs: 15, status: 'success' },
];
